import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Label } from '@/components/ui/label';
import { Globe, FileText, Search, ChevronDown, Check } from 'lucide-react';
import { getDomains } from '@/services/api';
import type { SyncTemplate, SimulateNewRuleRequest } from '@/types/price-rules';
import type { Domain } from '@/types/domain';

interface Props {
    templates: SyncTemplate[];
    formData: Partial<SimulateNewRuleRequest>;
    setFormData: (data: Partial<SimulateNewRuleRequest>) => void;
}

export default function StepDomain({ templates, formData, setFormData }: Props) {
    const [domainSearch, setDomainSearch] = useState('');
    const [domainOpen, setDomainOpen] = useState(false);

    const { data: domains, isLoading: domainsLoading } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    const activeTemplates = templates.filter(t => t.status === 'active');
    const selectedTemplate = templates.find(t => t.id === formData.template_id);
    const selectedDomain = domains?.find((d: Domain) => d.id === formData.domain_id);

    const filteredDomains = domains?.filter((d: Domain) =>
        d.domain.toLowerCase().includes(domainSearch.toLowerCase())
    ) || [];

    const handleDomainChange = (domainId: number) => {
        const domain = domains?.find((d: Domain) => d.id === domainId);
        setFormData({
            ...formData,
            domain_id: domainId,
            domain_name: domain?.domain || ''
        });
        setDomainOpen(false);
        setDomainSearch('');
    };

    const getFaviconUrl = (domain: string) => {
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Domínio e Template</h2>
                <p className="text-sm text-gray-500 mt-1">Selecione o domínio e o template de sincronização.</p>
            </div>

            {/* Seleção de Domínio */}
            <div className="space-y-3">
                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <div className="p-1.5 bg-blue-100 rounded-md">
                        <Globe className="h-4 w-4 text-blue-600" />
                    </div>
                    Domínio <span className="text-red-500">*</span>
                </Label>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setDomainOpen(!domainOpen)}
                        disabled={domainsLoading}
                        className="w-full h-12 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                    >
                        {selectedDomain ? (
                            <div className="flex items-center gap-3">
                                <img
                                    src={getFaviconUrl(selectedDomain.domain)}
                                    alt=""
                                    className="w-5 h-5 rounded"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                                <span>{selectedDomain.domain}</span>
                            </div>
                        ) : (
                            <span className="text-gray-500">{domainsLoading ? 'Carregando...' : 'Selecione um domínio'}</span>
                        )}
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${domainOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {domainOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => { setDomainOpen(false); setDomainSearch(''); }} />
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                <div className="p-2 border-b border-gray-100">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Buscar domínio..."
                                            value={domainSearch}
                                            onChange={(e) => setDomainSearch(e.target.value)}
                                            className="w-full h-10 pl-10 pr-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                                            onClick={(e) => e.stopPropagation()}
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <div className="max-h-[240px] overflow-y-auto">
                                    {filteredDomains.length === 0 ? (
                                        <div className="p-4 text-center text-sm text-gray-500">
                                            Nenhum domínio encontrado
                                        </div>
                                    ) : (
                                        filteredDomains.map((domain: Domain) => (
                                            <button
                                                key={domain.id}
                                                type="button"
                                                onClick={() => handleDomainChange(domain.id)}
                                                className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                    formData.domain_id === domain.id ? 'bg-blue-50' : ''
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={getFaviconUrl(domain.domain)}
                                                        alt=""
                                                        className="w-5 h-5 rounded"
                                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                    />
                                                    <span className={formData.domain_id === domain.id ? 'font-medium text-blue-700' : ''}>
                                                        {domain.domain}
                                                    </span>
                                                </div>
                                                {formData.domain_id === domain.id && (
                                                    <Check className="h-4 w-4 text-blue-600" />
                                                )}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Seleção de Template */}
            <div className="space-y-3">
                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <div className="p-1.5 bg-purple-100 rounded-md">
                        <FileText className="h-4 w-4 text-purple-600" />
                    </div>
                    Template de Sync <span className="text-red-500">*</span>
                </Label>

                {!formData.domain_id ? (
                    <div className="p-4 border-2 border-dashed border-gray-200 rounded-xl text-center">
                        <p className="text-sm text-gray-500">Selecione um domínio primeiro</p>
                    </div>
                ) : activeTemplates.length === 0 ? (
                    <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                        <p className="text-sm text-yellow-800">⚠️ Nenhum template ativo. Crie um template antes de adicionar regras.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        {activeTemplates.map((template) => (
                            <button
                                key={template.id}
                                type="button"
                                onClick={() => setFormData({ ...formData, template_id: template.id })}
                                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                                    formData.template_id === template.id
                                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                                }`}
                            >
                                {formData.template_id === template.id && (
                                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-purple-500 shadow flex items-center justify-center">
                                        <Check className="h-4 w-4 text-white" />
                                    </div>
                                )}
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">{template.name}</p>
                                        {template.description && (
                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.description}</p>
                                        )}
                                    </div>
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full shrink-0 ${
                                        template.sync_mode === 'automatic'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-700'
                                    }`}>
                                        {template.sync_mode === 'automatic' ? 'Auto' : 'Manual'}
                                    </span>
                                </div>
                                {template.scale_thresholds.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1">
                                        {template.scale_thresholds
                                            .sort((a, b) => a.difference_percentage - b.difference_percentage)
                                            .slice(0, 3)
                                            .map((t, i) => (
                                                <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                                    ≥{t.difference_percentage}% → ±{t.scale_percentage}%
                                                </span>
                                            ))}
                                        {template.scale_thresholds.length > 3 && (
                                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                                +{template.scale_thresholds.length - 3}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Resumo */}
            {(selectedDomain || selectedTemplate) && (
                <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Seleção atual</h4>
                    <div className="flex flex-wrap gap-3">
                        {selectedDomain && (
                            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                                <img
                                    src={getFaviconUrl(selectedDomain.domain)}
                                    alt=""
                                    className="w-4 h-4 rounded"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                                <span className="text-sm font-medium text-gray-900">{selectedDomain.domain}</span>
                            </div>
                        )}
                        {selectedTemplate && (
                            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                                <FileText className="h-4 w-4 text-purple-600" />
                                <span className="text-sm font-medium text-gray-900">{selectedTemplate.name}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}